import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import formatDate from '../../components/formatDate';
import {
  getMissionsPayload,
  getParticipantDetail,
} from '../../services/missionsApi';
import { formatNumber } from './missionUtils';
import { useMissionsSession } from './useMissionsSession';

function ParticipantDetail() {
  const { subjectType, subjectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const { handleApiResponse } = useMissionsSession(setError);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getParticipantDetail(subjectType, subjectId);
      if (handleApiResponse(result, 'Failed to load participant detail.')) {
        setDetail(null);
        return;
      }
      setDetail(getMissionsPayload(result));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load participant detail.');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [subjectType, subjectId, handleApiResponse]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const subject = detail?.subject;
  const active = detail?.active;
  const completedMissions = detail?.completed_missions || [];
  const recentActions = detail?.recent_actions || [];
  const stats = detail?.stats || {};

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton component={Link} to="/admin/missions/participants">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ ml: 1 }}>
            <Typography variant="h6">
              {subject?.display_name || subject?.id || subjectId}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" label={subject?.type || subjectType} />
              {(subject?.type === 'guest' || subjectType === 'guest') && (
                <Typography variant="caption" color="text.secondary">
                  Guest hash: {subject?.id || subjectId}
                </Typography>
              )}
              {subject?.user_id && (
                <MuiLink
                  component={Link}
                  to={`/admin/fan-ledger/${subject.user_id}`}
                  variant="body2"
                >
                  Fan Ledger #{subject.user_id}
                </MuiLink>
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={loadDetail} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Progress stuck at 0 with an empty audit usually means the action never fired or the app
        <code> missions</code> flag is off. Lower-than-expected progress: compare <code>dedup_key</code>.
        Task complete but mission not: every task must be done for <code>completion_mode: all</code>.
        Guests stall on M4/M8/M12 until login (fan journey, comment, post).
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700}>
                {formatNumber(stats.total_missions_completed)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Missions Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700}>
                {formatNumber(stats.total_actions_recorded)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Actions Recorded
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Active Mission
          </Typography>
          {!active ? (
            <Typography color="text.secondary">No active mission.</Typography>
          ) : (
            <>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {active.mission_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {active.stage} · Assigned {formatDate(active.assigned_at)}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={active.progress_percentage ?? 0} />
                <Typography variant="caption">{active.progress_percentage ?? 0}% complete</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(active.tasks || []).map((task) => {
                      const target = Number(task.target_value) || 0;
                      const current = Number(task.current_value) || 0;
                      const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
                      return (
                        <TableRow key={task.requirement_id}>
                          <TableCell>{task.label || task.action_key}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {task.action_key}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <LinearProgress variant="determinate" value={pct} sx={{ mb: 0.5 }} />
                            <Typography variant="caption">
                              {current}/{target}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={task.completed ? 'success' : 'default'}
                              label={task.completed ? 'Done' : 'In progress'}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Completed Missions
          </Typography>
          {completedMissions.length === 0 ? (
            <Typography color="text.secondary">No completed missions yet.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Mission</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell align="right">FE Earned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completedMissions.map((m) => (
                    <TableRow key={m.user_mission_id}>
                      <TableCell>{m.mission_name}</TableCell>
                      <TableCell>{m.stage}</TableCell>
                      <TableCell>{formatDate(m.completed_at)}</TableCell>
                      <TableCell align="right">{m.fan_energy_earned}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Action Audit
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last 200 deduped events — use dedup_key to debug progress mismatches
          </Typography>
          {recentActions.length === 0 ? (
            <Typography color="text.secondary">No recent actions recorded.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Mission</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Dedup Key</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActions.map((action) => (
                    <TableRow key={action.id}>
                      <TableCell>{formatDate(action.created_at)}</TableCell>
                      <TableCell>{action.action_key}</TableCell>
                      <TableCell>{action.mission_name}</TableCell>
                      <TableCell>{action.user_mission_status}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                          {action.dedup_key}
                        </Typography>
                        {action.metadata != null && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {typeof action.metadata === 'string'
                              ? action.metadata
                              : JSON.stringify(action.metadata)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ParticipantDetail;
